/**
 * Camera model for the onboarding right-panel preview.
 *
 * Steps 1–4 live on a single DashboardMockup canvas; the "camera" frames a
 * region of it by mapping a focus point (in dashboard design coordinates) to the
 * centre of the viewport at a given scale. Step 5 swaps to the MembersMockup.
 */

import type { Transition } from "framer-motion";

/** Right-panel viewport (matches the Figma frame: lg:w-1/2, full height). */
export const VIEWPORT_W = 519;
export const VIEWPORT_H = 654;

/** Natural design size of the dashboard canvas (landscape ~1.63). */
export const DASH_W = 1320;
export const DASH_H = 810;

type Focus = {
  /** screen the camera is looking at */
  screen: "dashboard";
  /** focus point in dashboard design coords */
  fx: number;
  fy: number;
  /** zoom level */
  scale: number;
};

/**
 * Per-step camera. Keyed by wizard step (1–5).
 *  1 About you      → left side, zoomed out
 *  2 Org profile    → top navbar / workspace badge
 *  3 First project  → left sidebar, tight
 *  4 Customize      → left side, slight zoom
 *  5 Choose plan    → zoomed-out overview of the dashboard
 */
export const STEP_CAMERA: Record<number, Focus> = {
  1: { screen: "dashboard", fx: 300, fy: 410, scale: 0.9 },
  2: { screen: "dashboard", fx: 1200, fy: 120, scale: 1.2 },
  3: { screen: "dashboard", fx: 118, fy: 300, scale: 1.0 },
  4: { screen: "dashboard", fx: 250, fy: 410, scale: 1.28 },
  5: { screen: "dashboard", fx: 660, fy: 405, scale: 0.78 },
};

const clamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(v, min), max);

/**
 * Translate so the focus point lands in the viewport centre, with the dashboard
 * transform-origin at its top-left (0 0):
 *   x = vw/2 - fx*s ,  y = vh/2 - fy*s
 *
 * The result is clamped so the scaled canvas always covers the viewport — the
 * camera can never reveal empty background past the dashboard edges. This lets
 * focus points sit near an edge (e.g. the top navbar) and still frame cleanly.
 */
export function cameraTransform(step: number) {
  const cam = STEP_CAMERA[step] ?? STEP_CAMERA[1];
  const { scale, fx, fy } = cam;
  return {
    scale,
    x: clamp(VIEWPORT_W / 2 - fx * scale, VIEWPORT_W - DASH_W * scale, 0),
    y: clamp(VIEWPORT_H / 2 - fy * scale, VIEWPORT_H - DASH_H * scale, 0),
  };
}

export function screenForStep(step: number): "dashboard" {
  return (STEP_CAMERA[step] ?? STEP_CAMERA[1]).screen;
}

/** Shared glide — settles without overshoot wobble. */
export const CAMERA_SPRING: Transition = {
  type: "spring",
  stiffness: 120,
  damping: 22,
  mass: 0.9,
};
